// In-app notification service: talks to the SASSP notification endpoints and maps the backend
// NotificationResponse shape onto the UI notification item shape the panel in Container.js renders.
//
// Auth + base URL follow the same convention as the other SASSP slices (see AssetApprovalSlice):
// DTOP-API-TOKEN + USERNAME headers from cookies, base URL from GetProxy().
import Cookies from "universal-cookie";
import moment from "moment";
import { handleResponseCode } from "../../app/utilities/helpers";
import GetProxy from "../../app/GetProxy";

const cookies = new Cookies();
const proxy = GetProxy();

const authHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  "DTOP-API-TOKEN": cookies.get("apitoken"),
  USERNAME: cookies.get("username"),
});

/** GET the current user's recent notifications (read + unread, newest first). */
export const fetchNotifications = async () => {
  const response = await fetch(`${proxy}/sassp/notifications`, {
    method: "GET",
    headers: authHeaders(),
  });
  handleResponseCode(response);
  return response.json();
};

/** GET the current user's unread badge count: { count }. */
export const fetchUnreadCount = async () => {
  const response = await fetch(`${proxy}/sassp/notifications/unread-count`, {
    method: "GET",
    headers: authHeaders(),
  });
  handleResponseCode(response);
  return response.json();
};

/** PUT mark a single notification read. */
export const markNotificationRead = async (id) => {
  const response = await fetch(`${proxy}/sassp/notifications/${id}/read`, {
    method: "PUT",
    headers: authHeaders(),
  });
  handleResponseCode(response);
  return response.json();
};

/** PUT mark all of the current user's notifications read. */
export const markAllNotificationsRead = async () => {
  const response = await fetch(`${proxy}/sassp/notifications/read-all`, {
    method: "PUT",
    headers: authHeaders(),
  });
  handleResponseCode(response);
  return response.json();
};

/** DELETE a single notification (the panel's per-item Dismiss). */
export const deleteNotification = async (id) => {
  const response = await fetch(`${proxy}/sassp/notifications/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  handleResponseCode(response);
  // The endpoint may answer 204/empty, so don't insist on a JSON body.
  const body = await response.text();
  return body ? JSON.parse(body) : null;
};

/** DELETE every notification belonging to the current user (the panel's "Clear all"). */
export const deleteAllNotifications = async () => {
  const response = await fetch(`${proxy}/sassp/notifications/delete-all`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  handleResponseCode(response);
  // The endpoint may answer 204/empty, so don't insist on a JSON body.
  const body = await response.text();
  return body ? JSON.parse(body) : null;
};

// Maps a backend notification `type` to the UI's visual metadata (icon colour + filter category).
// Extend this table as new notification types are added on the backend.
const TYPE_TO_UI = {
  ASSET_APPROVAL_REQUEST: {
    type: "warning",
    category: "approval",
    actionLabel: "Review Now",
  },
  ASSET_APPROVED: {
    type: "success",
    category: "approval",
    actionLabel: "View Asset",
  },
};

const uiMeta = (backendType) =>
  TYPE_TO_UI[backendType] || { type: "info", category: "system" };

const toGroup = (createdAt) =>
  moment(createdAt).isSame(moment(), "day") ? "today" : "earlier";

/** Backend NotificationResponse -> the notification item shape the panel expects. */
export const transformNotification = (item) => {
  const meta = uiMeta(item.type);
  return {
    id: item.id,
    title: item.title || "Notification",
    message: item.message || "",
    time: item.createdAt ? moment(item.createdAt).fromNow() : "",
    read: !!item.read,
    type: meta.type,
    category: meta.category,
    group: toGroup(item.createdAt),
    priority: meta.category === "approval" ? "high" : "low",
    ...(meta.actionLabel ? { actionLabel: meta.actionLabel } : {}),
    referenceId: item.referenceId,
    referenceType: item.referenceType,
  };
};

/** Live WebSocket payload (see backend NotificationService.pushLive) -> UI notification item. */
export const buildLiveNotification = (payload) =>
  transformNotification({
    id: payload.id,
    title: payload.title,
    message: payload.message,
    createdAt: payload.createdAt,
    read: false,
    type: payload.type,
    referenceId: payload.referenceId,
    referenceType: payload.referenceType,
  });
